import { ChevronDown, ChevronRight } from "lucide-react";
import { Fragment, useMemo, useState, type ReactNode } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  groupObjectiveDetail,
  objectiveTypeKey,
  type ObjectiveDetailRow,
  type ObjectiveSummaryRow,
} from "../data/cockpit-data";
import {
  formatObjectiveProfitYield,
  sortObjectiveSummaryRows,
} from "../data/objective-profitability";
import { OBJECTIVE_BAND_LABEL, badgeVariant, objectiveBandTone } from "../format";

/**
 * Profitability by objective and campaign type, with each row expanding to the
 * campaigns underneath it.
 *
 * One objective can be profitable in aggregate while a single market inside it
 * loses money every day, so the detail is not decoration — it is where the
 * answer to "why" lives. It is collapsed by default because nine columns times
 * every campaign is not a table anyone reads.
 */
export function ObjectiveGroups({
  summary,
  detail,
}: {
  summary: ObjectiveSummaryRow[];
  detail: ObjectiveDetailRow[];
}) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const groupedDetail = useMemo(() => groupObjectiveDetail(detail), [detail]);
  const rows = useMemo(() => sortObjectiveSummaryRows(summary), [summary]);

  return (
    <Card>
      <CardContent className="flex flex-col gap-4">
        <h3 className="text-base font-semibold text-ks-ink">
          Profitability by objective and campaign type
        </h3>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Objective</TableHead>
                <TableHead>Campaign type</TableHead>
                <TableHead>Band</TableHead>
                <TableHead className="text-right">Spend</TableHead>
                <TableHead className="text-right">Sales</TableHead>
                <TableHead className="text-right">Profit</TableHead>
                <TableHead className="text-right">Profit yield</TableHead>
                <TableHead className="text-right">ACoS</TableHead>
                <TableHead className="text-right">Conforming</TableHead>
                <TableHead>Markets</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const key = objectiveTypeKey(row.objective, row.campaign_type);
                const isExpanded = expanded === key;
                const markets = groupedDetail.get(key) ?? new Map<string, ObjectiveDetailRow[]>();
                const marginBlended =
                  (row.margin_imputed_share ?? 0) > 0 || (row.margin_unknown_share ?? 0) > 0;
                return (
                  <Fragment key={key}>
                    <TableRow>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="-mx-2 max-w-full justify-start text-left font-medium text-ks-ink"
                          aria-expanded={isExpanded}
                          onClick={() => setExpanded(isExpanded ? null : key)}
                        >
                          {isExpanded ? <ChevronDown aria-hidden="true" /> : <ChevronRight aria-hidden="true" />}
                          <span className="truncate">{row.objective}</span>
                        </Button>
                      </TableCell>
                      <TableCell className="font-medium">{row.campaign_type ?? "n/a"}</TableCell>
                      <TableCell>
                        <Badge variant={badgeVariant(objectiveBandTone(row.band))}>
                          {OBJECTIVE_BAND_LABEL[row.band]}
                        </Badge>
                      </TableCell>
                      <NumberCell>{fmtMoney(row.spend)}</NumberCell>
                      <NumberCell>{fmtMoney(row.sales)}</NumberCell>
                      <TableCell className="text-right tabular-nums">
                        <div className="flex flex-col items-end gap-1.5">
                          <span>{fmtMoney(row.profit)}</span>
                          {marginBlended && (
                            <Badge
                              variant="outline"
                              title="Sponsored Brands rows carry no advertised ASIN, so their margin is blended at market level."
                            >
                              margin blended
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <NumberCell>{formatObjectiveProfitYield(row.profit_yield)}</NumberCell>
                      <NumberCell>{fmtPercent(row.acos)}</NumberCell>
                      <NumberCell>{fmtConforming(row.conforming, row.judged)}</NumberCell>
                      <TableCell className="text-muted-foreground">{row.markets ?? "n/a"}</TableCell>
                    </TableRow>
                    {isExpanded && (
                      <TableRow>
                        <TableCell colSpan={10}>
                          <ObjectiveDetail markets={markets} />
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

function NumberCell({ children }: { children: ReactNode }) {
  return <TableCell className="text-right tabular-nums">{children}</TableCell>;
}

function ObjectiveDetail({ markets }: { markets: Map<string, ObjectiveDetailRow[]> }) {
  const entries = [...markets.entries()].sort(([left], [right]) =>
    left.localeCompare(right, undefined, { numeric: true, sensitivity: "base" }),
  );
  if (entries.length === 0) {
    return (
      <p className="py-4 text-[13px] font-medium text-muted-foreground">No campaign detail returned.</p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {entries.map(([market, rows]) => (
        <div key={market} className="rounded-lg border border-ks-grey-3">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-ks-grey-3 px-4 py-3">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{market}</h4>
            <span className="text-xs tabular-nums text-muted-foreground">{rows.length} campaigns</span>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Campaign</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead className="text-right">Spend</TableHead>
                  <TableHead className="text-right">Sales</TableHead>
                  <TableHead className="text-right">Profit</TableHead>
                  <TableHead className="text-right">Profit yield</TableHead>
                  <TableHead className="text-right">ACoS</TableHead>
                  <TableHead>Conformance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow key={`${row.campaign_name}:${index}`}>
                    <TableCell>
                      <div className="font-medium text-ks-ink">{row.campaign_name}</div>
                      {row.account && <div className="text-xs text-muted-foreground">{row.account}</div>}
                    </TableCell>
                    <TableCell>
                      <Badge variant={row.is_live ? "secondary" : "outline"}>
                        {row.is_live ? "LIVE" : "PAUSED"}
                      </Badge>
                    </TableCell>
                    <NumberCell>{fmtMoney(row.spend)}</NumberCell>
                    <NumberCell>{fmtMoney(row.sales)}</NumberCell>
                    <NumberCell>{fmtMoney(row.profit)}</NumberCell>
                    <NumberCell>{formatObjectiveProfitYield(row.profit_yield)}</NumberCell>
                    <NumberCell>{fmtPercent(row.acos)}</NumberCell>
                    <TableCell>
                      <div className="flex flex-col gap-2">
                        <ConformanceChip status={row.conformance_status} />
                        {row.conformance_reasons.length > 0 && (
                          <span className="max-w-md text-xs leading-5 text-muted-foreground">
                            {row.conformance_reasons.join(", ")}
                          </span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      ))}
    </div>
  );
}

function ConformanceChip({ status }: { status: string | null }) {
  const normalized = status?.toUpperCase() ?? null;
  const variant =
    normalized === "CONFORMING" ? "secondary" : normalized === "NON-CONFORMING" ? "destructive" : "outline";
  return <Badge variant={variant}>{normalized ?? "analysis pending"}</Badge>;
}

function fmtMoney(value: number | null): string {
  return value === null
    ? "n/a"
    : value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Scale Insights stores ACoS as an already-percentage number (e.g. 28.95 = 28.95%),
// so it is formatted as a plain number with a % suffix. Do NOT use Intl style:'percent'.
function fmtPercent(value: number | null): string {
  return value === null ? "n/a" : `${value.toLocaleString("en-US", { maximumFractionDigits: 1 })}%`;
}

function fmtConforming(conforming: number | null, judged: number | null): string {
  if (conforming === null) return "n/a";
  return judged === null ? fmtCount(conforming) : `${fmtCount(conforming)} / ${fmtCount(judged)}`;
}

function fmtCount(value: number): string {
  return value.toLocaleString("en-US", { maximumFractionDigits: 0 });
}
